// web/src/routes/dashboard/ClientDashboard.jsx
// Today, for a client. Answers: "How's my horse, and when do I ride next?"
//
// The planner idiom, with one deliberate exception: the horses keep their
// photographs and their card. A client opens this to look at their horse, and
// flattening that into a ruled line to match the other panels would be matching
// for its own sake — the photo is the reason the card exists.
//
// Lessons lead. "When do I ride next" is the question somebody opens the app
// to answer; the horses are reference and are still right underneath.
//
// The page flips. The lessons run FORWARD from the day you turn to rather than
// showing only that day's — a client has nothing on most days, and a page that
// went blank on the 3rd would answer "when do I ride next" with silence. The
// horse cards don't move: "last ridden yesterday" is a fact about now, and
// re-anchoring it to a day you flipped to would make it a lie.

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import SmartImg from '../../components/ui/SmartImg';
import { colorClass } from '../../lib/lessonTypes';
import { fmtDate } from '../../lib/dates';
import { clock } from '../../lib/shifts';
import MiniMonth from '../../components/dashboard/MiniMonth';
import { DayHeader, Panel, Empty, Row, Rows, PlannerGrid, PlannerSkeleton } from '../../components/dashboard/Planner';

export default function ClientDashboard() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'client', date ?? 'today'],
    queryFn: () => api.get(date ? `/dashboard/client?date=${date}` : '/dashboard/client'),
    placeholderData: (prev) => prev,
  });

  const today = data?.today ?? null;
  const shown = data?.date ?? date ?? today;
  const horses = data?.horses ?? [];

  const goTo = (d) => {
    const next = new URLSearchParams(params);
    if (!d || d === today) next.delete('date');
    else next.set('date', d);
    setParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <DayHeader
        today={today}
        date={shown}
        onToday={() => goTo(null)}
        action={
          <Link to="/schedule/book" className="text-sm text-hunter hover:underline pb-1">
            Book a lesson →
          </Link>
        }
      />

      {isLoading ? (
        <PlannerSkeleton />
      ) : (
        <PlannerGrid
          aside={
            <MiniMonth
              today={today}
              selected={shown}
              onPick={goTo}
              // The days they are actually riding, dotted — the one thing a
              // client wants off a month at a glance.
              marked={(data?.upcomingLessons ?? []).map((l) => l.date)}
            />
          }
        >
          <UpcomingLessons lessons={data?.upcomingLessons} shown={shown} today={today} />

          {horses.length === 0 ? (
            <Panel title="My horses">
              <Empty>No horses are linked to your account yet.</Empty>
            </Panel>
          ) : (
            <Panel title={horses.length === 1 ? 'My horse' : 'My horses'} count={horses.length}>
              <ul className="planner-rows">
                {horses.map((h) => (
                  <li key={h.id}>
                    <HorseRow horse={h} />
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </PlannerGrid>
      )}
    </div>
  );
}

function UpcomingLessons({ lessons = [], shown, today }) {
  // On today, "in 3 days" is the useful form. On a page you turned to, it is
  // measured from that page — so it reads correctly there and would read as a
  // lie next to the word "today" at the top. Off today, name the day instead.
  const onToday = !shown || !today || shown === today;
  return (
    <Panel
      title={onToday ? 'Upcoming lessons' : 'Lessons from here'}
      action={
        <Link to="/schedule/book" className="normal-case tracking-normal hover:underline">
          book →
        </Link>
      }
    >
      {lessons.length === 0 ? (
        <Empty>
          Nothing scheduled yet. <Link to="/schedule/book" className="text-hunter hover:underline">Book a lesson →</Link>
        </Empty>
      ) : (
        <Rows>
          {lessons.map((l) => (
            <Row key={l.id} to={l.horseId ? `/horses/${l.horseId}` : '/schedule/book'} className="flex items-baseline gap-3">
              <span className="w-24 shrink-0">
                <span className="block font-medium text-hunter">
                  {onToday ? l.relative : fmtDate(l.date, 'dayShort')}
                </span>
                {l.startTime && (
                  <span className="block text-xs text-ink-400 tabular-nums">{clock(l.startTime)}</span>
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {l.lessonTypeName && (
                    <span className={'inline-block w-2 h-2 rounded-full mr-1.5 ' + colorClass(l.lessonTypeColor, 'dot')} />
                  )}
                  {[l.riderName, l.horseName].filter(Boolean).join(' · ')}
                </span>
                <span className="block text-xs text-ink-400">
                  {[l.lessonTypeName, l.trainerName].filter(Boolean).join(' · ')}
                </span>
              </span>
              {l.status === 'tentative' && (
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  Tentative
                </span>
              )}
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}

function HorseRow({ horse }) {
  return (
    <Link to={`/horses/${horse.id}`} className="planner-row flex gap-3">
      <Avatar url={horse.photoUrl} name={horse.name} />
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline justify-between gap-2">
          <span className="font-semibold text-base truncate">{horse.name}</span>
          {horse.stall && <span className="text-xs text-ink-400 shrink-0">stall {horse.stall}</span>}
        </span>

        {horse.lastRide && (
          <span className="block text-ink-600">
            Last ridden <span className="text-ink-800">{horse.lastRide.relative}</span>
            {horse.lastRide.rideType && <span className="text-ink-400"> · {horse.lastRide.rideType}</span>}
          </span>
        )}

        {horse.latestNote && (
          <span className="block mt-1 pl-2 border-l-2 border-hunter/25 text-ink-700 line-clamp-2">
            {horse.latestNote.body}
            <span className="text-xs text-ink-400"> — {horse.latestNote.authorName}, {horse.latestNote.relative}</span>
          </span>
        )}

        {horse.upcomingHealth?.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {horse.upcomingHealth.map((h, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {h.recordType} due {fmtDate(h.dueDate, 'short')}
              </span>
            ))}
          </span>
        )}
      </span>
    </Link>
  );
}

function Avatar({ url, name }) {
  if (url) {
    return (
      <SmartImg src={url} width={80} alt={name}
        className="w-16 h-16 rounded-md object-cover shrink-0 bg-ink-100" />
    );
  }
  return (
    <span className="w-16 h-16 rounded-md bg-hunter/10 flex items-center justify-center text-2xl shrink-0">
      🐴
    </span>
  );
}
